/**
 * Footer Hooks - Extended with siteId lookup
 *
 * @override
 * - Re-exports all base footer hooks from @amblique/contentful
 * - Adds useFooterBySiteId hook for fetching footer by site identifier
 */

import {print} from 'graphql';

import {useQuery} from '@tanstack/react-query';

import {useService} from '@amblique/base/app/hooks/use-service';
import {ContentfulService} from '@amblique/contentful/app/services/contentful-service';
import {
  getBaseFooterBySiteId,
  getFooterBySiteId,
  getFullFooterBySiteId,
  getFooterWithColumnsBySiteId,
} from '@amblique/king-living/app/contentful/queries/footer.queries';

// Re-export all base hooks
export * from '@amblique/contentful/app/contentful/hooks/use-footer';

/**
 * Hook for fetching a Footer by its siteId field
 *
 * @param {string} siteId - The site identifier (e.g., 'kl-au', 'kl-nz')
 * @param {Object} [options={}] - Hook options
 * @param {'base'|'default'|'full'} [options.variant='default'] - Query variant (base: scalars only, default: 1-level deep, full: 2-levels deep)
 * @param {Object} [options.variables={}] - Additional GraphQL query variables
 * @param {import('@tanstack/react-query').UseQueryOptions<Contentful.Footer|null, Error>} [options.queryOptions={}] - React Query options
 * @param {RequestInit} [options.requestOptions={}] - HTTP request options (headers, cache, etc.)
 * @returns {import('@tanstack/react-query').UseQueryResult<Contentful.Footer|null, Error>}
 *
 * @example
 * // Basic usage (default variant)
 * const { data: footer, isLoading } = useFooterBySiteId('kl-au');
 *
 * @example
 * // With variant selection
 * const { data } = useFooterBySiteId('kl-nz', {
 *   variant: 'full',  // Get complete nested data
 *   variables: { preview: true }
 * });
 *
 * @example
 * // With all options
 * const { data } = useFooterBySiteId('kl-au', {
 *   variant: 'base',  // Fast, scalars only
 *   variables: { preview: true },
 *   queryOptions: { staleTime: 0 },
 *   requestOptions: { cache: 'no-store' }
 * });
 */
export function useFooterBySiteId(siteId, options = {}) {
  const contentfulService = useService(ContentfulService);

  const {
    variant = 'default',
    variables = {},
    queryOptions = {},
    requestOptions = {},
  } = options;

  // Select query based on variant
  // 'withColumns' uses custom query with explicit limits to avoid Contentful complexity errors
  const query = variant === 'base' ?
    getBaseFooterBySiteId :
    variant === 'full' || variant === 'withColumns' ?
      getFooterWithColumnsBySiteId :
      getFooterBySiteId;

  return useQuery({
    queryKey: [
      contentfulService?.key,
      'footer',
      'siteId',
      siteId,
      variant,
      contentfulService?.locale,
      JSON.stringify(variables)
    ],

    queryFn: async () => {
      if (!contentfulService) {
        throw new Error('ContentfulService not available');
      }

      const data = await contentfulService.query(
        print(query),
        {
          siteId,
          locale: contentfulService?.locale,
          preview: false,
          ...variables,
        },
        requestOptions
      );

      return data.footerCollection?.items?.[0] || null;
    },

    enabled: !!contentfulService && !!siteId,

    staleTime: 10 * 60 * 1000, // 10 minutes
    gcTime: 15 * 60 * 1000, // 15 minutes
    refetchOnWindowFocus: false,

    retry: (failureCount, error) => {
      // Don't retry on 4xx errors
      if (error?.status >= 400 && error?.status < 500) {
        return false;
      }

      return failureCount < 3;
    },

    ...queryOptions,
  });
}
